<template>
  <DefaultLayout>
    <div ref="topdiv">
      <img v-lazy="getPhoto" class="img-fluid" />
      <div class="text-center">
        <br />
        <h4>{{ getTitle }}</h4>
      </div>

      <FadeTransition>
        <div v-if="videoPlaying">
          <video autoplay width="100%" id="video" @ended="videoEnded()">
            <source :src="videoUrl" />
          </video>
        </div>
      </FadeTransition>
      <div v-if="showScenario">
        <div>
          <span v-html="getDescription" />
          <br />
          <br />

          <div class="text-center" id="question">
            <strong>{{ getCaseQuestion }}</strong>
          </div>

          <card shadow v-if="showRelations && playerTips()">
            <small>
              <span v-html="playerTips()" />
            </small>
          </card>

          <div class="text-center">
            <small v-if="!showRelations">
              {{ $t("selectcolleague") }}
            </small>
            <div class="text-center">
              <b-button-group v-for="player in $store.getters.getPlayers" :key="player.id">
                <base-button :type="getButtonType(player.id)" :aria-pressed="buttonPressed(player.id)"
                  :class="getActiveClass(player.id)" :disabled="disablePlayerSelectButton"
                  v-on:click="nominationDone(player.id)" style="margin: 2px" cursor="getCursorType()">
                  {{ player.nick_name }}
                  <font-awesome-icon :icon="getIcon(player.id)"></font-awesome-icon>
                  <font-awesome-icon v-if="player.paused" icon="pause"></font-awesome-icon> </base-button>&nbsp;
              </b-button-group>
            </div>

            <small v-if="!showRelations" class="text-center">
              {{ $t("symbolhelp") }}
              <br />
              <font-awesome-icon icon="clock" />
              = {{ $t("clock") }},
              <font-awesome-icon icon="check" />
              = {{ $t("check") }}
              <template v-if="showPauseCaption">
                ,
                <font-awesome-icon icon="pause" />
                = {{ $t("pause") }}
              </template>
            </small>
          </div>

          <div class="row justify-content-center" ref="relationscontainer" v-if="$store.state.main.allPlayers.length">
            <div :class="getRelationsClass()" ref="relationsholder">
              <collapse-transition :duration="2000">
                <div v-show="showRelations" id="relations">
                  <resize-observer @notify="handleResize" />
                  <RelationsCytoscape :key="relationsComponentKey" :initialCanvasWidth="relationsHolderWidth"
                    :selectedCaseNumber="currentCaseNumber" />
                  <small>
                    <div style="line-height: 1.2">
                      {{ $t("graphexplanation") }} '{{ selfEvaluationQuestion }}'
                    </div>
                  </small>
                  <SelfEvaluationColorbar />
                  <base-checkbox class="mb-3" v-model="showPriorityLabels">{{
                    $t("prioritylabels")
                  }}</base-checkbox>
                </div>
              </collapse-transition>

              <div v-if="!showRelations">
                <base-progress type="default" :value="getProgress()" :label="$t('finishedplayers')"></base-progress>
              </div>
            </div>
          </div>
        </div>

        <br />

        <collapse-transition :duration="1000">
          <div v-show="showSurvey" id="surveys">
            <div class="text-center" v-if="!showSurveyAverages">
              <small>
                {{ $t("surveytips") }}
              </small>
              <br />
              <br />
            </div>

            <Survey v-if="showSurvey1" :selectedCaseNumber="currentCaseNumber" :survey="1" />

            <div class="text-center">
              <small>
                <SurveyWaitingPlayers :survey="1" />
              </small>
            </div>

            <template v-if="surveyAlreadyAnswered1">
              <b>{{ getSurveyQuestion(1) }}</b>
              {{ surveyDescription }}
              <span v-if="!showSurveyAverages">{{ $t("surveyanswered") }}<br /></span>
            </template>

            <collapse-transition :duration="1000">
              <div v-show="showSurveyAverages">
                {{ $t("surveyaverage") }} = <b>{{ surveyAverage1 }}</b> ({{ $t("lowest") }}:
                {{ surveyLowest1 }} - {{ $t("highest") }}: {{ surveyHighest1 }})
                <br />
              </div>
            </collapse-transition>
            <br />
            <Survey v-if="showSurvey2" :selectedCaseNumber="currentCaseNumber" :survey="2" />

            <div class="text-center">
              <small>
                <SurveyWaitingPlayers :survey="2" />
              </small>
            </div>

            <template v-if="surveyAlreadyAnswered2">
              <b>{{ getSurveyQuestion(2) }}</b>
              {{ surveyDescription }}
              <span v-if="!showSurveyAverages">{{ $t("surveyanswered") }}<br /></span>
            </template>
            <collapse-transition :duration="1000">
              <div v-show="showSurveyAverages">
                {{ $t("surveyaverage") }} = <b>{{ surveyAverage2 }}</b> ({{ $t("lowest") }}:
                {{ surveyLowest2 }} - {{ $t("highest") }}: {{ surveyHighest2 }})
              </div>
            </collapse-transition>
            <SurveyProgressbar />
            <br />
          </div>
        </collapse-transition>

        <span v-if="askComment">
          {{ $t("anycomments") }}
          <base-input maxLength="1000" ref="comment" id="input-comment" v-model="comment"
            v-on:keyup.native.enter="submitComment" :disabled="$store.state.main.playerIsOnlyFacilitator" />

          <div v-for="(aComment) in comments" :key="aComment.id">{{ aComment }}</div>
        </span>

        <FacilitatorComments v-if="$store.getters.getIsFacilitator" />

        <SuggestionBoxInfo v-if="currentCaseNumber === 1" />

        <div class="text-center">
          <base-button size="sm" type="secondary" v-on:click="previousCase()" v-if="showPreviousCase">{{
            $t("previouscase")
            }}</base-button>

          <span v-if="!showNextCase && $store.getters.getIsFacilitator">
            <span id="disabled-wrapper" class="d-inline-block" tabindex="0" v-b-tooltip.hover
              :title="facilitatorNextButtonTooltip">
              <base-button size="lg" type="primary" v-on:click="nextCase()" disabled>{{
                nextButtonText
              }}</base-button>
            </span>
          </span>
          <span v-else-if="
            !showNextCase && $store.getters.getThisPlayer && $store.getters.getThisPlayer.paused
          ">
            <span id="disabled-wrapper-paused-player" class="d-inline-block" tabindex="0" v-b-tooltip.hover
              :title="$t('paused-player-next-button-tooltip')">
              <base-button size="lg" type="primary" v-on:click="nextCase()" disabled>{{
                nextButtonText
              }}</base-button>
            </span>
          </span>
          <span v-else-if="showNextCase">
            <base-button size="lg" type="primary" v-on:click="nextCase()">{{
              nextButtonText
            }}</base-button>
          </span>

          <LastPagePausedPlayerInfo v-if="showNextCase" />

          <br />
          {{ $t("case") }} {{ currentCaseNumber }} /
          {{ casesLength }}
        </div>
        <br />
        <NewPlayerPausing />
        <Robot />
        <SuggestionBox :currentCaseNumber="currentCaseNumber" :isClassicGame="true" />
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import Swal from "sweetalert2";
import { mapState, mapGetters } from "vuex";
import {
  sendNomination,
  sendComment,
  getSurveyAverage,
  updateGameCurrentCaseNumberForPlayers,
  removePlayerPause
} from "../services/APIService";
import SelfEvaluationColorbar from "../components/SelfEvaluationColorbar.vue";
import RelationsCytoscape from "@/components/RelationsCytoscape.vue";
import SurveyProgressbar from "@/components/SurveyProgressbar.vue";
import Survey from "@/components/Survey.vue";
import { getSurveyQuestion, generateSurveyDescriptionString, convertPhotoToAmazonURL } from "../services/utils.js";
import NewPlayerPausing from "@/components/NewPlayerPausing.vue";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import Robot from "@/components/Robot.vue";
import FacilitatorComments from "@/components/FacilitatorComments.vue";
import SurveyWaitingPlayers from "@/components/SurveyWaitingPlayers.vue";
import { FadeTransition } from "vue2-transitions";
import LastPagePausedPlayerInfo from "@/components/LastPagePausedPlayerInfo.vue";
import SuggestionBox from "@/components/SuggestionBox.vue";
import SuggestionBoxInfo from "@/components/SuggestionBoxInfo.vue";

const VueScrollTo = require("vue-scrollto");

export default {
  components: {
    SelfEvaluationColorbar,
    RelationsCytoscape,
    Survey,
    NewPlayerPausing,
    SurveyProgressbar,
    DefaultLayout,
    Robot,
    SurveyWaitingPlayers,
    FacilitatorComments,
    FadeTransition,
    LastPagePausedPlayerInfo,
    SuggestionBox,
    SuggestionBoxInfo
  },
  updated() {
    this.handleResize();
  },
  data: function () {
    return {
      relationsHaveBeenShown: false,
      relationsComponentKey: 0,
      relationsHolderWidth: 0,
      dataReady: false,
      comment: "",
      comments: [],
      showSurvey1: false,
      showSurvey2: false,
      surveyAlreadyAnswered1: false,
      surveyAlreadyAnswered2: false,
      surveyAverage1: 0,
      surveyLowest1: 0,
      surveyHighest1: 0,
      surveyAverage2: 0,
      surveyLowest2: 0,
      surveyHighest2: 0,
      currentCaseNumber: 1,
      videoPlaying: false,
      showScenario: true,
      videoUrl: "",
      answerSent: 0
    };
  },
  watch: {
    showRelations: {
      handler(newData, oldData) {
        this.$store.dispatch("log", "showRelations watcher");
        if (newData && !oldData) {
          this.$nextTick(() => {
            VueScrollTo.scrollTo("#question");
          });
        }
      }
    },
    getPlayers: {
      handler(newData, oldData) {
        this.$store.dispatch("log", "getPlayers watcher");
        // New player or existing player's data have paused, update relations
        this.relationsComponentKey++;

        // If pause status has changed for someone, let's update averages
        this.updateAverages();

        // Let's check if this player was unpaused and is in another case than where the other players are.
        // If so, jump to the same case where the other players are.
        if (this.$store.getters.getThisPlayer && !this.$store.getters.getThisPlayer.paused) {
          if (this.$store.state.main.game.currentCaseNumberForPlayers !== this.currentCaseNumber) {
            this.$store.dispatch("log", "Player unpaused, taking back to current case for players");
            this.$router.push("/game/" + this.$store.state.main.game.currentCaseNumberForPlayers);
          }
        }
      },
      deep: true
    },

    getSelfEvaluations: {
      handler(newData, oldData) {
        this.$store.dispatch("log", "getSelfEvaluations watcher");

        // New self evaluation, update relations
        this.relationsComponentKey++;
      },
      deep: true
    },

    getNominationsForCurrentCase: {
      handler(newData, oldData) {
        this.$store.dispatch("log", "getNominationsForCurrentCase watcher");
        // Nomination change for this case, update relations
        this.relationsComponentKey++;
      },
      deep: true
    },

    getGameCaseOptionsForCurrentCase: {
      handler(newData, oldData) {
        // Surveys enabled and not facilitator and relations shown? Scroll!
        if (!this.showRelations) {
          return;
        }

        // Don't scroll if we are in a different case than others
        if (this.$store.getters.getCurrentCaseNumberForPlayers !== this.currentCaseNumber) {
          return;
        }

        // Might have changed in a different case...
        const options = this.$store.getters.getGameCaseOptionsForCase(
          this.$store.getters.getCurrentCase.case
        );

        if (options && options[0] && options[0].show_survey_questions) {
          this.$nextTick(() => {
            VueScrollTo.scrollTo("#surveys");
          });
        }
      },
      deep: true
    },

    getSurveyAnswers: {
      handler(newData, oldData) {
        // Survey answers changed, update...
        this.updateAverages();
      }
    },

    getCurrentCaseNumberForPlayers: {
      handler(newData, oldData) {
        if (this.$store.state.main.game.reportingOpen) {
          // If reporting has been opened, facilitator wants to browse freely, players can't enter
          return;
        }
        if (
          this.$store.getters.getThisPlayer &&
          this.$store.getters.getThisPlayer.paused &&
          newData <= this.$store.getters.getNumberOfCases
        ) {
          // Paused players don't proceed, UNLESS game proceeds to last page
          return;
        }

        if (this.currentCaseNumber < newData) {
          // We should move to next case
          this.$store.dispatch("log", "New current case for players: " + newData);

          this.$store.commit({
            type: "setCurrentCaseNumber",
            currentCaseNumber: newData
          });

          if (this.comment !== "") {
            this.$store.commit({
              type: "setUnfinishedComment",
              unfinishedComment: this.comment
            });
          }

          if (newData <= this.$store.getters.getNumberOfCases) {
            this.gotoCase(newData);
          } else {
            this.gotoEnd();
          }
        }
      }
    },
    getReportingOpen: {
      handler(newData, oldData) {
        if (newData) {
          // A corner case: if a paused player player
          // is in some case and reporting is opened,
          // throw the player to reporting
          this.$router.push("/reporting");
        }
      }
    }
  },
  async created() {
    if (
      this.$store.state.main.game.currentCaseNumberForPlayers > this.$store.getters.getNumberOfCases
    ) {
      // Throw also paused players to last page if game closed
      this.gotoEnd();
      return;
    }

    if (this.$store.state.main.game.reportingOpen) {
      // Player probably used back button -> push to reporting
      await this.$router.push("/reporting");
    }

    // Let's get case from URL
    let currentCaseNumber = parseInt(this.$route.params.case);

    if (!currentCaseNumber) {
      currentCaseNumber = 1;
    }

    if (
      (this.$store.getters.getIsFacilitator || this.$store.state.main.playerIsObserver) &&
      this.$store.state.main.game.currentCaseNumberForPlayers !== currentCaseNumber
    ) {
      await this.$router.push("/game/" + this.$store.state.main.game.currentCaseNumberForPlayers);
      return;
    }

    if (this.$store.getters.getThisPlayer && !this.$store.getters.hasThisPlayerDoneSelfEvaluation) {
      // Hmmm. Probably trying to enter game by the game URL. Let's throw back to self evaluation.
      await this.$router.push("/selfevaluation");
      return;
    }

    if (this.$store.getters.getThisPlayer && !this.$store.getters.getThisPlayer.paused) {
      // Only paused players can be in a different case than others.
      if (currentCaseNumber !== this.$store.state.main.game.currentCaseNumberForPlayers) {
        this.gotoCase(this.$store.state.main.game.currentCaseNumberForPlayers);
        return;
      }
    }

    if (this.$store.getters.getThisPlayer && this.$store.getters.getThisPlayer.paused) {
      // Paused player
      const highestAnsweredCase = this.$store.getters.getLastAnsweredCaseForPlayer;
      // Let's check we don't go beyond the last case
      if (currentCaseNumber > highestAnsweredCase + 1) {
        let lastCase = this.$store.getters.getNumberOfCases;
        if (highestAnsweredCase + 1 < lastCase) {
          lastCase = highestAnsweredCase + 1;
        }

        await this.$router.push("/game/" + lastCase);
        return;
      }

      // Let's also check we don't go to a case too small:
      if (
        currentCaseNumber < this.$store.state.main.game.currentCaseNumberForPlayers &&
        highestAnsweredCase > currentCaseNumber
      ) {
        await this.$router.push("/game/" + highestAnsweredCase);
        return;
      }
    }

    this.$store.commit({
      type: "setCurrentCaseNumber",
      currentCaseNumber: currentCaseNumber
    });

    this.currentCaseNumber = currentCaseNumber;

    if (this.$store.state.main.game.showVideos && this.$store.getters.getCurrentCase.startVideo) {
      this.$store.dispatch("log", "Found video: " + this.$store.getters.getCurrentCase.startVideo);

      this.videoUrl = this.$store.getters.getCurrentCase.startVideo;
      this.showScenario = false;

      // A short delay before video is played, so that the header picture is shown
      setTimeout(() => {
        this.videoPlaying = true;
      }, 200);
    }

    this.updateAverages();

    this.$store.dispatch("connectNotification");

    this.$store.dispatch("updateGame");

    // Let's update players, nominations and self evaluations
    const p1 = this.$store.dispatch("updatePlayers");
    const p2 = this.$store.dispatch("updateSelfEvaluations");
    const p3 = this.$store.dispatch("updateAllNominations");

    const p4 = this.$store.dispatch("updateGameCaseOptions");

    if (this.$store.state.main.game.enableSurvey) {
      await this.$store.dispatch("updateSurveyAnswers");
    }

    if (
      this.$store.getters.getIsFacilitator &&
      !this.$store.state.main.playerIsObserver &&
      this.$store.state.main.game.enableComments
    ) {
      this.$store.dispatch("updateComments");
    }

    Promise.all([p1, p2, p3, p4]).then(resp => {
      // Let's check if we need to gather answers to surveys. This is true if:
      // - survey questions exists, AND
      // - player has not yet answered to the question

      if (this.$store.state.main.game.enableSurvey) {
        if (
          !this.$store.state.main.playerIsOnlyFacilitator &&
          !this.$store.state.main.playerIsObserver
        ) {
          const answer1 = this.$store.getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
            currentCaseNumber,
            1,
            this.$store.state.main.thisPlayerId
          );
          this.showSurvey1 = answer1.length === 0;
          this.surveyAlreadyAnswered1 = !this.showSurvey1;

          const answer2 = this.$store.getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
            currentCaseNumber,
            2,
            this.$store.state.main.thisPlayerId
          );

          this.showSurvey2 = answer2.length === 0;
          this.surveyAlreadyAnswered2 = !this.showSurvey2;
        } else {
          // For only facilitator, show always surveys if they exist (they will be disabled)
          this.showSurvey1 = true;
          this.showSurvey2 = true;
        }
      }
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.dataReady = true;
    });

    this.checkAndUnpausePlayer();

    if (this.$store.getters.getUnfinishedComment) {
      Swal.fire({
        text: this.$t("game-forwading-comment-stored"),
        showDenyButton: false,
        confirmButtonText: "ok",
        allowOutsideClick: false
      });

      this.comment = this.$store.getters.getUnfinishedComment;

      this.$store.commit({
        type: "setUnfinishedComment",
        unfinishedComment: null
      });
    }
  },
  computed: {
    ...mapState(["main.nominations"]),
    ...mapGetters([
      "getPlayers",
      "getSelfEvaluations",
      "getNominationsForCurrentCase",
      "getSurveyAnswers",
      "getCurrentCaseNumberForPlayers",
      "getGameCaseOptionsForCurrentCase"
    ]),
    showPriorityLabels: {
      set(showPriorityLabels) {
        this.$store.commit({
          type: "setShowPriorityLabels",
          showPriorityLabels: showPriorityLabels
        });
        this.relationsComponentKey++; // Force redraw
      },
      get() {
        return this.$store.state.main.showPriorityLabels;
      }
    },
    getReportingOpen() {
      return this.$store.state.main.game.reportingOpen;
    },
    disablePlayerSelectButton() {
      return (
        this.$store.state.main.playerIsOnlyFacilitator ||
        this.$store.state.main.playerIsObserver ||
        this.$store.state.main.game.reportingOpen
      );
    },
    getPhoto() {
      if (this.getCase(this.currentCaseNumber).photo) {
        return convertPhotoToAmazonURL(this.getCase(this.currentCaseNumber).photo);
      } else {
        return "/placeholder.png";
      }
    },
    getCases() {
      return this.$store.getters.getCasesInCurrentLocale;
    },
    casesLength() {
      return this.getCases.length;
    },
    getTitle() {
      return this.getCase(this.currentCaseNumber).title;
    },
    getDescription() {
      return this.getCase(this.currentCaseNumber).description;
    },
    getCaseQuestion() {
      return this.getCase(this.currentCaseNumber).caseQuestion;
    },
    isLastCase() {
      return this.casesLength === this.currentCaseNumber;
    },
    nextButtonText() {
      if (
        this.$store.getters.getIsFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        this.currentCaseNumber === this.$store.getters.getNumberOfCases
      ) {
        return this.$t("lastpagenextbuttontext");
      } else if (
        this.$store.getters.getIsFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        this.currentCaseNumber === this.$store.getters.getCurrentCaseNumberForPlayers
      ) {
        return this.$t("nextcaseeveryone");
      } else {
        return this.$t("nextcase");
      }
    },
    showNextCase() {
      if (this.$store.state.main.playerIsObserver) {
        return false;
      }

      if (!this.$store.getters.getIsFacilitator && !this.$store.getters.getThisPlayer.paused) {
        return false;
      }

      // Paused player:
      if (
        !this.$store.state.main.playerIsOnlyFacilitator &&
        this.$store.getters.getThisPlayer.paused
      ) {
        if (!this.hasPlayersAnsweredSurveys) {
          return false;
        }

        if (this.$store.getters.hasThisPlayerDoneNomination(this.currentCaseNumber)) {
          return true;
        } else {
          return false;
        }
      }
      // Only facilitator:
      const everyoneAnsweredSurveys =
        this.$store.getters.getNumberOfUnpausedPlayers === 0 ||
        (this.surveyAverage1 > 0 && this.surveyAverage2 > 0);
      if (!this.$store.getters.haveAllPlayersDoneNomination(this.currentCaseNumber)) {
        // don't show if all non-paused players have not done the nomination yet
        return false;
      } else if (this.$store.state.main.game.enableSurvey && !everyoneAnsweredSurveys) {
        // Survey exists and has not been answered by everyone
        return false;
      } else {
        // Everything is ok
        return true;
      }
    },
    showPreviousCase() {
      if (this.$store.getters.getCurrentCaseNumberForPlayers < this.currentCaseNumber) {
        // Player has been paused but is now unpaused, but has proceeded. Let her
        // return until reaches the same case where other players are.
        return true;
      } else {
        return false;
      }
    },
    askComment() {
      return this.$store.state.main.playerIsObserver
        ? false
        : this.$store.state.main.game.enableComments;
    },
    showPauseCaption() {
      let showPause = false;

      this.$store.state.main.allPlayers.forEach(player => {
        if (player.paused) {
          showPause = true;
        }
      });

      return showPause;
    },
    showSurvey() {
      if (!this.$store.state.main.game.enableSurvey) {
        return false;
      }

      // Show survey if player is paused AND has done nomination
      if (
        !this.$store.state.main.playerIsOnlyFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        this.$store.getters.getThisPlayer.paused
      ) {
        const myNomination = this.$store.getters.getSomePlayersNominationForCase(
          this.$store.state.main.thisPlayerId,
          this.currentCaseNumber
        );

        return myNomination;
        // This is null if hasn't yet done nomination
      }

      // If this player has already answered at least one survey, don't hide them
      if (this.$store.getters.hasThisPlayerAnsweredSurvey(this.currentCaseNumber, 1)) {
        return true;
      }

      if (this.$store.getters.hasThisPlayerAnsweredSurvey(this.currentCaseNumber, 2)) {
        return true;
      }

      // Has "show additional questions" been enabled?
      const options = this.$store.getters.getGameCaseOptionsForCase(
        this.$store.getters.getCurrentCase.case
      );

      if (options && options[0] && options[0].show_survey_questions) {
        return true;
      } else {
        return false;
      }
    },
    showSurveyAverages() {
      // Only show averages after all questions have been answered by everyone
      if (!this.$store.getters.haveAllUnpausedDoneSurvey(this.currentCaseNumber)) {
        return false;
      }

      if (this.surveyAverage1 > 0 && this.surveyAverage2 > 0) {
        return true;
      } else {
        return false;
      }
    },
    surveyDescription() {
      return generateSurveyDescriptionString(this.$store);
    },
    selfEvaluationQuestion() {
      const aCase = this.$store.getters.getCaseByCaseNumber(this.currentCaseNumber);

      return aCase.selfEvaluationQuestion;
    },
    showRelations() {
      if (!this.dataReady) {
        return false;
      } else if (this.relationsHolderWidth === 0) {
        return false;
      } else if (this.relationsHaveBeenShown) {
        // don't hide relations if somebody joins the game
        return true;
      } else if (
        !this.$store.state.main.playerIsOnlyFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        !this.$store.getters.hasThisPlayerDoneNomination(this.currentCaseNumber)
      ) {
        // don't show relations if this player has not yet done the nomination (haveAllPlayersDoneNomination takes into account all paused players so we have to check separately)
        return false;
      } else {
        // This is not pretty but if it works don't break it...
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.relationsHaveBeenShown = this.$store.getters.haveAllPlayersDoneNomination(
          this.currentCaseNumber
        );
        return this.relationsHaveBeenShown;
      }
    },
    facilitatorNextButtonTooltip() {
      if (!this.$store.getters.haveAllPlayersDoneNomination(this.currentCaseNumber)) {
        return this.$t("next-button-tooltip-nominations-missing");
      }

      const options = this.$store.getters.getGameCaseOptionsForCase(
        this.$store.getters.getCurrentCase.case
      );

      if (options && options[0] && options[0].show_survey_questions) {
        // Nominations are done but cannot proceed to survey has been enabled.
        // Survey has not been opend
        return this.$t("next-button-tooltip-survey-not-answered");
      }

      return this.$t("next-button-tooltip-survey-not-opened");
    },
    hasPlayersAnsweredSurveys() {
      if (this.$store.state.main.game.enableSurvey) {
        const s1 = this.$store.getters.hasThisPlayerAnsweredSurvey(this.currentCaseNumber, 1);
        const s2 = this.$store.getters.hasThisPlayerAnsweredSurvey(this.currentCaseNumber, 2);

        if (!s1 || !s2) {
          return false;
        }
      }
      return true;
    }
  },
  methods: {
    async checkAndUnpausePlayer() {
      if (
        this.$store.getters.getThisPlayer &&
        this.$store.getters.getThisPlayer.paused &&
        this.currentCaseNumber === this.$store.getters.getCurrentCaseNumberForPlayers
      ) {
        await removePlayerPause(
          this.$store.getters.getThisPlayer.id,
          this.$store.state.main.game.gameCode
        );
        await this.$store.dispatch("updatePlayers");

        Swal.fire({
          text: this.$t("reached_others"),
          confirmButtonText: this.$t("ok"),
          allowOutsideClick: false
        });
      }
    },
    getCase(caseNumber) {
      return this.$store.getters.getCaseByCaseNumber(caseNumber);
    },
    allowedToViewCase(caseNumber) {
      // Check if the requested case is ok
      if (caseNumber > this.casesLength) {
        this.$store.dispatch("log", "Too big case number");
        return false;
      }

      return true;
    },
    forceRerender() {
      this.componentKey += 1;
    },
    getProgress() {
      const currentCase = this.currentCaseNumber;
      const unpausedPlayers = this.$store.getters.getNumberOfUnpausedPlayers;

      if (unpausedPlayers === 0) {
        // Avoid division by zero if there are no players
        return 0;
      }

      return Math.round(
        (100 * this.$store.getters.howManyUnpausedPlayersHaveDoneNomination(currentCase)) /
        unpausedPlayers
      );
    },
    getIcon(playerId) {
      if (this.$store.getters.hasSomePlayerDoneNomination(this.currentCaseNumber, playerId)) {
        return "check";
      } else {
        return "clock";
      }
    },
    async nextCase() {
      if (this.$store.getters.getThisPlayer && this.$store.getters.getThisPlayer.paused) {
        this.submitComment();
      }

      if (this.comment !== "") {
        this.$store.commit({
          type: "setUnfinishedComment",
          unfinishedComment: this.comment
        });
      }

      const newCase = this.currentCaseNumber + 1;

      if (
        this.$store.getters.getIsFacilitator &&
        !this.$store.state.main.playerIsObserver &&
        newCase > this.$store.state.main.game.currentCaseNumberForPlayers
      ) {
        await updateGameCurrentCaseNumberForPlayers(
          this.$store.state.main.game.id,
          this.$store.state.main.game.gameCode,
          newCase
        );
        await this.$store.dispatch("updateGame");
        // force update and wait for it, otherwise we will have old data for a while in the next case and "previous case" button will be shown briefly
      }

      if (this.isLastCase) {
        this.gotoEnd();
      } else {
        this.gotoCase(newCase);
      }
    },
    previousCase() {
      if (this.$store.getters.getThisPlayer && this.$store.getters.getThisPlayer.paused) {
        this.submitComment();
      }

      const newCase = this.currentCaseNumber - 1;

      this.$store.commit({
        type: "setCurrentCaseNumber",
        currentCaseNumber: newCase
      });
      if (newCase === 0) {
        this.gotoBegin();
      } else {
        this.gotoCase(newCase);
      }
    },
    buttonPressed(nomineeId) {
      const myNomination = this.$store.getters.getSomePlayersNominationForCase(
        this.$store.state.main.thisPlayerId,
        this.currentCaseNumber
      );

      if (!myNomination) {
        // Has not yet done nomination
        return false;
      } else if (myNomination.nominee === nomineeId) {
        return true;
      } else {
        return false;
      }
    },
    getActiveClass(nomineeId) {
      // make button appear pressed
      return this.buttonPressed(nomineeId) ? "active" : "";
    },
    getButtonType(nomineeId) {
      // make button appear pressed
      return this.buttonPressed(nomineeId) ? "warning" : "primary";
    },
    nominationDone(nominee) {
      if (Date.now() - this.answerSent < 500) {
        // Prevent double click
        return;
      }

      this.answerSent = Date.now();

      this.$store.dispatch("log", "Sending nomination");
      // Nomination done, unpress other nominees' buttons. Keep the button player selected pressed, unpress all others.
      this.$store.state.main.allPlayers.forEach(player => {
        player.id === this.nominee ? (player.state = true) : (player.state = false);
      });

      const aCase = this.$store.getters.getCaseByCaseNumber(this.currentCaseNumber);

      sendNomination(
        this.$store.state.main.game.id,
        this.$store.state.main.thisPlayerId,
        nominee,
        aCase.case,
        this.$store.state.main.game.gameCode
      ).then(resp => {
        this.$store.dispatch({
          type: "updateNominationsIfPolling",
          caseId: this.$store.getters.getCurrentCase.case
        });
      });
    },
    submitComment() {
      if (this.comment.length > 0) {
        this.comments.push(this.comment);

        const aCase = this.$store.getters.getCaseByCaseNumber(this.currentCaseNumber);

        sendComment(
          this.$store.state.main.game.id,
          this.$store.state.main.thisPlayerId,
          this.comment,
          aCase.case,
          this.$store.state.main.game.gameCode
        ).then(resp => { });

        this.comment = "";
        this.$store.commit({
          type: "setUnfinishedComment",
          unfinishedComment: null
        });
      }
    },
    playerTips() {
      return this.$store.getters.getScenarioInCurrentLocale.player_tips;
    },
    updateAverages() {
      if (!this.$store.state.main.game.enableSurvey) {
        return;
      }

      const aCase = this.$store.getters.getCaseByCaseNumber(this.currentCaseNumber);

      // Let's check that this player has answered to the surveys (only relevant
      // for paused players who are behind other players)

      if (
        !this.$store.state.main.playerIsOnlyFacilitator &&
        !this.$store.state.main.playerIsObserver
      ) {
        if (
          this.$store.getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
            this.currentCaseNumber,
            1,
            this.$store.state.main.thisPlayerId
          ).length === 0
        ) {
          return;
        }

        if (
          this.$store.getters.getSurveyAnswersByCaseNumberSurveyNumberPlayerId(
            this.currentCaseNumber,
            2,
            this.$store.state.main.thisPlayerId
          ).length === 0
        ) {
          return;
        }
      }

      getSurveyAverage(
        this.$store.state.main.game.id,
        aCase.case,
        1,
        this.$store.state.main.game.gameCode
      ).then(data => {
        this.surveyAverage1 = data.average.toFixed(1);
        this.surveyLowest1 = data.lowest_value;
        this.surveyHighest1 = data.highest_value;
      });

      getSurveyAverage(
        this.$store.state.main.game.id,
        aCase.case,
        2,
        this.$store.state.main.game.gameCode
      ).then(data => {
        this.surveyAverage2 = data.average.toFixed(1);
        this.surveyLowest2 = data.lowest_value;
        this.surveyHighest2 = data.highest_value;
      });
    },
    getSurveyQuestion(surveyNumber) {
      return getSurveyQuestion(surveyNumber, this.currentCaseNumber, this.$store);
    },
    getCursorType() {
      if (
        this.$store.state.main.playerIsOnlyFacilitator ||
        this.$store.state.main.playerIsObserver
      ) {
        return "initial"; // don't change cursor type in case only facilitator
      } else {
        return "auto";
      }
    },
    handleResize() {
      if (
        this.$refs.relationsholder &&
        this.relationsHolderWidth !== this.$refs.relationsholder.offsetWidth
      ) {
        this.relationsHolderWidth = this.$refs.relationsholder.offsetWidth;
        this.$bus.$emit("relations-width-change", this.relationsHolderWidth);
      }
    },
    getRelationsClass() {
      if (!this.$refs.relationscontainer || this.$store.state.main.allPlayers.length > 11) {
        return "col-12";
      }

      if (this.$refs.relationscontainer.offsetWidth > 900) {
        return "col-8";
      } else if (this.$refs.relationscontainer.offsetWidth > 700) {
        return "col-10";
      } else {
        return "col-12";
      }
    },
    async gotoCase(caseNumber) {
      // Prevent double navigation error
      if (this.$router.currentRoute.path !== "/game/" + caseNumber) {
        await this.$router.push("/game/" + caseNumber);
      }
    },
    async gotoBegin() {
      if (this.$router.currentRoute.path !== "/begin") {
        await this.$router.push("/begin");
      }
    },
    async gotoEnd() {
      if (this.$router.currentRoute.path !== "/end") {
        await this.$router.push("/end");
      }
    },
    videoEnded() {
      this.videoPlaying = false;
      // Transitioning the video takes 300 milliseconds so let's show the scenario a bit later
      setTimeout(() => {
        this.showScenario = true;
      }, 350);
    },
  }
};
</script>
<i18n>
{
  "en": {
    "nextcase": "Next",
    "nextcaseeveryone": "Forward (all will proceed)",
    "previouscase": "Previous case",
    "finishedplayers": "Answered",
    "case": "case",
    "prioritylabels": "Show self-evaluation also using numbers",
    "anycomments": "Topics we should discuss or points to remember? Write your comment and press enter. Only the facilitator sees the comments, but not name of the commentator.",
    "surveyanswered": "You have already answered to the survey.",
    "selectcolleague": "Choose below the person who would be the most suitable fit for the question asked. If there are different shades of meaning in the story and in the question, put more emphasis on the bolded question.",
    "symbolhelp": "The symbol after the name indicates who have already made their choice.",
    "clock": "has not yet done the selection",
    "check": "done",
    "pause": "paused",
    "graphexplanation": "The arrows indicate the nominations. The colour of the oval shows the answer to the self-evaluation. The claim was:",
    "surveyaverage": "Average of responses",
    "lowest": "lowest",
    "highest": "highest",
    "next-button-tooltip-nominations-missing": "All players have not yet selected a person.",
    "next-button-tooltip-survey-not-answered": "All players have not yet answered additional questions.",
    "next-button-tooltip-survey-not-opened": "Please click 'show additional questions' button on Facilitator tools.",
    "game-forwading-comment-stored": "Simulation has moved to the next case. Your unsubmitted comment has been saved, and you can continue editing your comment in the next case.",
    "ok": "OK",
    "surveytips": "Please answer the following questions. Your answers are not shown to the other players.",
    "lastpagenextbuttontext": "Close the simulation (all will proceed)",
    "paused-player-next-button-tooltip": "Please answer the questions first.",
    "reached_others": "You have reached the same case as other players. From now on, the facilitator will control proceeding in the simulation."
  },
  "fi": {
    "nextcase": "Eteenpäin",
    "nextcaseeveryone": "Eteenpäin (kaikki siirtyvät)",    
    "previouscase": "Edellinen tilanne",
    "finishedplayers": "Vastanneet",
    "case": "tilanne",
    "prioritylabels": "Näytä itsearviointi myös numerona",
    "anycomments": "Mistä meidän tulisi keskustella aiheeseen liittyen? Kirjoita kommentti ja paina rivinvaihtoa. Vain pelinjohtaja näkee kommentit, mutta ei niiden kirjoittajaa.",
    "surveyanswered": "Vastauksesi kyselyyn on jo tallennettu.",
    "selectcolleague": "Valitse alta parhaiten kysymykseen sopiva henkilö. Jos tarinassa ja kysymyksessä on mielestäsi sävyeroja, painota lihavoitua kysymystä.",
    "symbolhelp": "Nimen perässä oleva symboli kertoo, ketkä ovat jo tehneet valinnan.",
    "clock": "ei ole vielä tehnyt valintaa",
    "check": "valmis",
    "pause": "tauolla",
    "graphexplanation": "Nuolet kuvaavat tehdyt nimeämiset. Soikion väri näyttää itsearvioinnin. Väittämä oli:",
    "surveyaverage": "Vastausten keskiarvo",
    "lowest": "alin",
    "highest": "korkein",
    "next-button-tooltip-nominations-missing": "Kaikki pelaajat eivät ole vielä valinneet sopivaa henkilöä.",
    "next-button-tooltip-survey-not-answered": "Kaikki pelaajat eivät ole vielä vastanneet lisäkysymyksiin.",
    "next-button-tooltip-survey-not-opened": "Paina 'näytä lisäkysymykset' -nappia Pelinjohtajan työkaluissa.",
    "game-forwading-comment-stored": "Simulaatiossa siirrytään seuraavaan tilanteeseen. Olet kirjoittanut kommentin, jota ei ole lähetetty. Kommentti on kopioitu seuraavaan tilanteeseen.",
    "ok": "OK",
    "surveytips": "Vastaa vielä seuraaviin kysymyksiin. Osallistujat eivät näe toistensa vastauksia.",
    "lastpagenextbuttontext": "Sulje simulaatio (kaikki siirtyvät)",
    "paused-player-next-button-tooltip": "Vastaa kysymyksiin ensin.",
    "reached_others": "Olet saavuttanut saman tilanteen kuin muut pelaajat. Pelinjohtaja vastaa tästä eteenpäin simulaatiossa etenemisestä."
  },
  "sv": {
    "nextcase": "Nästa",
    "nextcaseeveryone": "Framåt (alla går vidare)",    
    "previouscase": "Föregående",
    "finishedplayers": "De som svarat",
    "case": "fall",
    "prioritylabels": "Visa självskattningen även med siffror",
    "anycomments": "Ämnen vi borde diskutera eller viktiga punkter för att komma ihåg? Skriv din kommentar och tryck på enter. Endast spelansvarig ser kommentarerna, men inte vem som kommenterat.",
    "surveyanswered": "Dina svar på enkäten har redan sparats.",
    "selectcolleague": "Välj nedan den person som skulle vara den mest lämpliga personen för frågan. Om det finns utrymme för tolkningar i berättelsen jämfört med i frågan, lägg mer tonvikt på frågan i fetstil.",
    "symbolhelp": "Symbolen efter namnet indikerar vem som redan har gjort sitt val.",
    "clock": "har ännu inte gjort sitt val",
    "check": "klart",
    "graphexplanation": "Pilarna anger nomineringarna. Färgen i  den ovala formen visar svaret på självskattningen. Påståendet var:",
    "surveyaverage": "Genomsnitt av svar",
    "lowest": "lägst",
    "highest": "högst",
    "next-button-tooltip-nominations-missing": "Alla spelare har ännu inte valt en person.",
    "next-button-tooltip-survey-not-answered": "Alla spelare har ännu inte svarat på ytterligare frågor.",
    "next-button-tooltip-survey-not-opened": "Vänligen klicka på knappen 'visa ytterligare frågor' i Facilitatorverktygen.",
    "game-forwading-comment-stored": "Simuleringen har flyttats till nästa fall. Din ej inskickade kommentar har sparats och du kan fortsätta redigera din kommentar i nästa fall.",
    "ok": "OK",
    "surveytips": "Vänligen svara på följande frågor. Dina svar visas inte för de andra spelarna.",
    "lastpagenextbuttontext": "Stäng simuleringen (alla går vidare)",    
    "paused-player-next-button-tooltip": "Svara först på frågorna.",
    "reached_others": "Du har nått samma fall som andra spelare. Från och med nu kommer facilitatorn att kontrollera hur simuleringen fortskrider."
  },
 "de": {
    "nextcase": "Weiter",
    "nextcaseeveryone": "Vorwärts (alle werden verschoben)",    
    "previouscase": "Zurück",
    "finishedplayers": "Beantwortet",
    "case": "Fall",
    "prioritylabels": "Selbstevaluation auch mit Zahlen anzeigen",
    "anycomments": "Themen, die wir diskutieren sollten, oder Punkte, an die wir uns erinnern sollten? Schreiben Sie Ihren Kommentar und drücken Sie die Eingabetaste. Nur der Spielmanager sieht die Kommentare, nicht jedoch der Name des Kommentators.",
    "surveyanswered": "Sie haben die Umfrage bereits beantwortet.",
    "selectcolleague": "Wählen Sie unten die Person aus, der für die gestellte Frage am besten geeignet ist. Wenn die Geschichte und die Frage unterschiedliche Bedeutungsnuancen haben, legen Sie mehr Wert auf die fettgedruckte Frage.",
    "symbolhelp": "Das Symbol nach dem Namen zeigt an, wer bereits seine Wahl getroffen hat.",
    "clock": "hat die Auswahl noch nicht getroffen",
    "check": "erledigt",
    "graphexplanation": "Die Pfeile geben die Nominierungen an. Die Farbe der ovalen Form zeigt die Antwort auf der Selbstbewertung. Der Anspruch war:",
    "surveyaverage": "Durchschnitt der Antworten",
    "lowest": "am niedrigsten",
    "highest": "höchste",
    "next-button-tooltip-nominations-missing": "Alle Spieler haben noch keine Person ausgewählt.",
    "next-button-tooltip-survey-not-answered": "Alle Spieler haben noch keine zusätzlichen Fragen beantwortet.",
    "next-button-tooltip-survey-not-opened": "Bitte klicken Sie in den Facilitator-Tools auf die Schaltfläche 'Weitere Fragen anzeige'.",
    "game-forwading-comment-stored": "Die Simulation ist zum nächsten Fall übergegangen. Ihr nicht gesendeter Kommentar wurde gespeichert und Sie können Ihren Kommentar im nächsten Fall weiter bearbeiten.",
    "ok": "Ok",
    "surveytips": "Bitte beantworten Sie die folgenden Fragen. Ihre Antworten werden den anderen Spielern nicht angezeigt.",
    "lastpagenextbuttontext": "Schließen Sie die Simulation (alles wird fortgesetzt)",
    "paused-player-next-button-tooltip": "Bitte beantworten Sie zuerst die Fragen.",
    "reached_others": "Sie haben den gleichen Fall wie andere Spieler erreicht. Von nun an steuert der Spielleiter das Fortschreiten in der Simulation."    
  },
  "ko": {
    "nextcase": "다음",
    "nextcaseeveryone": "다음 (전원 다음 주제로 이동)",
    "previouscase": "이전",
    "finishedplayers": "응답 완료",
    "case": "주제",
    "prioritylabels": "진단 결과 숫자로 표시하기",
    "anycomments": "주제와 관련해서 추가적으로 다루었으면 하는 내용은 무엇인가요? 댓글로 의견을 제시해주세요. 내용을 작성한 뒤 엔터를 누르시면 익명으로 강의자에게 전달됩니다.",
    "surveyanswered": "설문에 이미 참여하셨습니다.",
    "selectcolleague": "질문에 가장 적합하다고 생각하는 사람을 아래에서 선택해주세요. 굵은 글씨로 표시된 질문에 집중하여 선택해주십시오.",
    "symbolhelp": "이름 옆의 표시는 응답의 상태를 나타냅니다.",
    "clock": "고민 중",
    "check": "선택 완료",
    "pause": "일시정지",
    "graphexplanation": "화살표는 누가 누구를 지목했는지를 나타냅니다. 타원의 색상은 스스로 진단한 결과를 표시합니다. 관련 문항: ",
    "surveyaverage": "응답 평균점수",
    "lowest": "최저",
    "highest": "최고",
    "next-button-tooltip-nominations-missing": "팀원 선택을 완료하지 않은 참여자가 있습니다.",
    "next-button-tooltip-survey-not-answered": "추가질문에 답변하지 않은 참여자가 있습니다.",
    "next-button-tooltip-survey-not-opened": "퍼실리테이터 도구의 '추가질문  표시'를 클릭하세요.",
    "ok": "네",
    "surveytips": "다음 질문에 응답해주세요. 개별 응답결과는 공개되지 않습니다.",
    "lastpagenextbuttontext": "시뮬레이션 닫기(모두 진행됨)",
    "paused-player-next-button-tooltip": "먼저 질문에 답변해 주십시오.",
    "reached_others": "다른 플레이어와 동일한 주제에 도달했습니다. 이제부터 진행자가 시뮬레이션 진행을 제어합니다."    
  }  
}
</i18n>
